import { createPortal } from 'react-dom';
import type { ReactNode } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select.js';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu.js';
import {
  GenerationCatalogPagination,
  useGenerationCatalogPage,
} from './GenerationCatalogPagination.js';
import { KnowledgeSearchSummary } from './KnowledgeSearchSummary.js';
import { GenerationDraftWarnings } from './GenerationDraftWarnings.js';
import {
  GenerationPreconditions,
  type GenerationCredentialContext,
} from './GenerationCaseText.js';
import { GenerationBasket } from './GenerationBasket.js';
import { GenerationVersionBadge } from './GenerationVersionBadge.js';
import { GenerationEvidence } from './GenerationEvidence.js';
import {
  previewGenerationBasket,
  resumeGenerationPublication,
  type GenerationBasketSelection,
  type GenerationSourceReceipt,
  type GenerationPreviewVariable,
} from '@/lib/api/quality-generation-v4.js';
import { useQuery } from '@tanstack/react-query';
import {
  CircleAlertIcon,
  ChevronDownIcon,
  CheckCircle2Icon,
  Link2Icon,
  MessageSquareIcon,
  SparklesIcon,
  XIcon,
  InfoIcon,
} from 'lucide-react';
import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type JSX,
  type Ref,
} from 'react';

import { AppDialog } from '@/components/ui/app-dialog.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card.js';
import { Checkbox } from '@/components/ui/checkbox.js';
import { Textarea } from '@/components/ui/textarea.js';
import {
  QualityApiError,
  runProposalsQueryOptions,
  useApproveRun,
  useCreateGeneratedCasePublicationBatch,
  useRequestChanges,
} from '@/lib/api/quality.js';
import {
  runOutputTargetLabel,
  type CreateGeneratedCasePublicationBatchBody,
  type GeneratedCasePublication,
  type GeneratedCasePublicationDestination,
  type ProposedStoryLink,
  type ProposalCase,
  type Run,
  type RunProposal,
} from '@/lib/api/quality-types.js';
import { notify } from '@/lib/notify.js';
import { safeHttpUrl } from '@/lib/safe-url.js';

import { CaseCoverage } from './CaseCoverage.js';
import { GenerationCaseSteps } from './GenerationCaseSteps.js';
import {
  GenerationMetaReview,
  isGenerationDestinationPublishable,
} from './GenerationMetaReview.js';
import {
  defaultPublicationDestination,
  isCompletePublicationDestination,
  PublicationDestinationPicker,
} from './PublicationDestinationPicker.js';
import { runLifecycleStatus } from './quality-outcome.js';

function storyLinkRefs(links?: ProposedStoryLink[]): string[] {
  return (links ?? []).map((link) => link.ref);
}

function publicationIdempotencyKey(): string {
  const bytes = new Uint8Array(16);
  const cryptoApi = globalThis.crypto;
  if (cryptoApi && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;
  const hex = [...bytes].map((value) => value.toString(16).padStart(2, '0'));
  return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10).join('')}`;
}

function publicationDestinationLabel(
  destination: GeneratedCasePublicationDestination | null,
  fallback: string,
): string {
  if (!destination) return fallback;
  if (destination.provider === 'own-tms') {
    return destination.suiteName
      ? `Quality suite ${destination.suiteName}`
      : 'Quality suite';
  }
  if (destination.provider === 'azure') {
    return `${fallback} · ${destination.planName ?? destination.planId} / ${destination.suiteName ?? destination.suiteId}`;
  }
  if (destination.provider === 'testrail') {
    return `${fallback} · ${destination.sectionName ?? `section ${destination.sectionId}`}`;
  }
  if (destination.provider === 'zephyr') {
    return `${fallback} · ${destination.folderName ?? destination.folderId ?? 'project root'}`;
  }
  const target =
    destination.label ??
    destination.folderPath ??
    destination.testSetId ??
    destination.testPlanId ??
    'project root';
  return `${fallback} · ${target}`;
}

/**
 * Mirrors the server's `publicationRowSucceeded` (run.serializer.ts): a publication only counts as
 * a completed success when it was created (or already present) AND its placement AND every retained
 * relation each succeeded or skipped. Used only by the client fallback below — the primary path
 * reads the server's own `available_case_indices`, already computed against this same rule.
 */
function publicationSucceeded(publication: GeneratedCasePublication): boolean {
  return (
    (publication.case_state === 'created' ||
      publication.case_state === 'skipped_existing') &&
    (publication.placement_state === 'succeeded' ||
      publication.placement_state === 'skipped') &&
    publication.relation_outcomes.every(
      (outcome) => outcome.state === 'succeeded' || outcome.state === 'skipped',
    )
  );
}

/**
 * The fallback path (used when the server doesn't send `available_case_indices`) filters through
 * `publicationSucceeded`, so a failed or ambiguous publication never claims its index either — it
 * must stay re-offerable, the same rule the server's `available_case_indices` already applies.
 */
function availableCaseIndices(proposal: RunProposal): number[] {
  if (proposal.available_case_indices) return proposal.available_case_indices;
  if (
    proposal.status === 'approved' &&
    !proposal.publications?.length &&
    !proposal.batches?.length
  ) {
    return [];
  }
  const claimed = new Set(
    proposal.publications
      ? proposal.publications
          .filter(publicationSucceeded)
          .map((publication) => publication.case_index)
      : (proposal.created_case_indices ?? []),
  );
  return proposal.cases
    .map((_, index) => index)
    .filter((index) => !claimed.has(index));
}

// The human-in-the-loop review surface for a 'generation' run: shows the current proposed cases,
// lets the reviewer approve (→ written to the run's output TMS) or request changes with per-case +
// overall feedback (→ a refine round). Prior versions and the feedback that produced them are below.
export function ReviewProposedCases({
  runId,
  run,
  canPublishRetainedDrafts,
  feedbackContainer,
  onOpenAssistant,
  generationNotice,
}: {
  runId: string;
  run: Run;
  canPublishRetainedDrafts?: boolean;
  feedbackContainer?: HTMLDivElement | null;
  onOpenAssistant?: () => void;
  generationNotice?: string | null;
}): JSX.Element {
  // Name the actual output target (Azure DevOps / Zephyr / TestRail) in the copy — never hardcode one.
  const targetLabel = runOutputTargetLabel(run);
  const {
    data: proposals = [],
    isLoading,
    isError,
    refetch,
  } = useQuery(runProposalsQueryOptions(runId));
  const createBatch = useCreateGeneratedCasePublicationBatch(runId);
  const approveRun = useApproveRun(runId);
  const requestChanges = useRequestChanges(runId);
  const legacyXrayNewContainer = Boolean(
    run.xray_output_project_key &&
    (run.xray_output_new_test_set_summary ||
      run.xray_output_new_test_plan_summary),
  );

  const current = useMemo(
    () =>
      proposals.reduce<(typeof proposals)[number] | undefined>(
        (latest, proposal) =>
          !latest || proposal.version > latest.version ? proposal : latest,
        undefined,
      ),
    [proposals],
  );
  const available = useMemo(
    () => (current ? availableCaseIndices(current) : []),
    [current],
  );
  const availableSignature = available.join(',');
  const canRequestChanges =
    runLifecycleStatus(run) === 'awaiting_review' &&
    current?.status === 'proposed' &&
    current.cases.length > 0 &&
    (current.refinement_state === undefined ||
      current.refinement_state === 'available');
  const canCreateCases =
    canPublishRetainedDrafts ??
    (run.outcome_protocol_version === 1
      ? run.execution_status === 'awaiting_review'
      : run.status === 'awaiting_review' || run.status === 'passed');
  const canResumePublication =
    run.execution_status !== 'cancelled' && run.status !== 'cancelled';

  const selectionKey = current
    ? `${runId}:${current.id}:${current.version}`
    : null;
  const catalog = useGenerationCatalogPage(
    selectionKey,
    available,
    available.length <= 10,
  );
  const [initializedSelectionKey, setInitializedSelectionKey] = useState<
    string | null
  >(null);
  const [variableChoices, setVariableChoices] = useState<
    GenerationBasketSelection['choices'] | null
  >(null);
  const [initializedVariableKey, setInitializedVariableKey] = useState<
    string | null
  >(null);
  const [variableNamespaceRevision, setVariableNamespaceRevision] = useState(0);
  const [resumingBatch, setResumingBatch] = useState<string | null>(null);
  const [overall, setOverall] = useState('');
  const feedbackRef = useRef<HTMLTextAreaElement>(null);
  const feedbackTriggerRef = useRef<HTMLButtonElement | null>(null);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackCaseIndex, setFeedbackCaseIndex] = useState<number | null>(
    null,
  );
  const [focusCaseIndex, setFocusCaseIndex] = useState<number | null>(null);
  const caseTitleRefs = useRef(new Map<number, HTMLButtonElement>());
  const [comments, setComments] = useState<Record<number, string>>({});
  // Which cases the reviewer has checked. Approve creates only these; Request-changes refines only
  // these (the rest carried forward unchanged). Defaults to all-checked on each new version.
  const [selected, setSelected] = useState<Set<number>>(new Set());
  // Reviewer-retained story refs are local to the current proposal version. The only v1 edit is
  // removal; an explicit empty array is preserved so approve can send remove-all unambiguously.
  const [retainedRefsByCase, setRetainedRefsByCase] = useState<
    Record<number, string[]>
  >({});
  const [includeSuggestedStoryLinks, setIncludeSuggestedStoryLinks] =
    useState(true);
  const [destination, setDestination] =
    useState<GeneratedCasePublicationDestination | null>(null);
  // Approve & create opens a final-confirmation dialog first — once cases are created they (and the
  // ones left uncreated) can no longer be edited.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const confirmedPreviewDigest = useRef<string | null>(null);
  const [metaOpen, setMetaOpen] = useState(false);
  const metaTriggerRef = useRef<HTMLButtonElement>(null);
  const pendingBatch = useRef<{
    fingerprint: string;
    request: CreateGeneratedCasePublicationBatchBody & { proposalId: string };
  } | null>(null);
  const v4 = Boolean(current?.generationMeta?.frozenInput);
  const variableNamespaceKey = `${selectionKey}:${availableSignature}:${JSON.stringify(current?.publications?.map((row) => [row.case_index, row.case_state, row.placement_state]) ?? [])}:${variableNamespaceRevision}`;
  const basketSelection: GenerationBasketSelection = {
    selectedIndices: (initializedSelectionKey === selectionKey
      ? [...selected].filter((index) => available.includes(index))
      : [...available]
    ).sort((a, b) => a - b),
    choices: (initializedVariableKey === variableNamespaceKey
      ? (variableChoices ?? [])
      : []
    )
      .filter(
        (choice) =>
          selected.has(choice.caseIndex) &&
          available.includes(choice.caseIndex),
      )
      .map((choice) => ({
        caseIndex: choice.caseIndex,
        variables: choice.variables
          .map((variable) => ({
            key: variable.key,
            reuseExisting: variable.reuseExisting === true,
          }))
          .sort((a, b) => a.key.localeCompare(b.key)),
        ...(choice.disabledExistingKeys?.length
          ? { disabledExistingKeys: [...choice.disabledExistingKeys].sort() }
          : {}),
      }))
      .sort((a, b) => a.caseIndex - b.caseIndex),
  };
  const basket = useQuery({
    queryKey: [
      'quality',
      runId,
      current?.id,
      'basket',
      current?.version,
      variableNamespaceKey,
      basketSelection,
    ],
    queryFn: () =>
      previewGenerationBasket(
        runId,
        current!.id,
        current!.version,
        basketSelection,
      ),
    enabled:
      v4 &&
      initializedSelectionKey === selectionKey &&
      Boolean(current?.cases.length) &&
      available.length > 0,
    retry: false,
  });
  const basketReady =
    !v4 ||
    (variableChoices !== null &&
      initializedVariableKey === variableNamespaceKey &&
      basket.data !== undefined &&
      basket.data?.version === current?.version &&
      JSON.stringify(basket.data.selection) ===
        JSON.stringify(basketSelection) &&
      !basket.isFetching &&
      !basket.isError);
  function toggleVariable(
    variable: GenerationPreviewVariable,
    indices: number[],
    enabled: boolean,
  ) {
    setConfirmOpen(false);
    setVariableChoices((previous) => {
      const choices = new Map(
        (previous ?? []).map((choice) => [choice.caseIndex, choice.variables]),
      );
      for (const index of indices) {
        const variables = (choices.get(index) ?? []).filter(
          (item) => item.key !== variable.key,
        );
        if (enabled)
          variables.push({
            key: variable.key,
            ...(variable.state === 'reuse_candidate'
              ? { reuseExisting: true }
              : {}),
          });
        choices.set(index, variables);
      }
      return [...choices].map(([caseIndex, variables]) => ({
        caseIndex,
        variables,
        ...(previous?.find((choice) => choice.caseIndex === caseIndex)
          ?.disabledExistingKeys?.length
          ? {
              disabledExistingKeys: previous!.find(
                (choice) => choice.caseIndex === caseIndex,
              )!.disabledExistingKeys,
            }
          : {}),
      }));
    });
  }
  function toggleExistingVariable(
    key: string,
    indices: number[],
    enabled: boolean,
  ) {
    setConfirmOpen(false);
    setVariableChoices((previous) => {
      const choices = new Map(
        (previous ?? []).map((choice) => [choice.caseIndex, choice]),
      );
      for (const caseIndex of indices) {
        const previousChoice = choices.get(caseIndex) ?? {
          caseIndex,
          variables: [],
        };
        const disabled = new Set(previousChoice.disabledExistingKeys ?? []);
        if (enabled) disabled.delete(key);
        else disabled.add(key);
        const { disabledExistingKeys: _previousKeys, ...choice } =
          previousChoice;
        choices.set(caseIndex, {
          ...choice,
          ...(disabled.size
            ? { disabledExistingKeys: [...disabled].sort() }
            : {}),
        });
      }
      return [...choices.values()];
    });
  }
  const busy =
    createBatch.isPending ||
    approveRun.isPending ||
    requestChanges.isPending ||
    resumingBatch !== null;
  const destinationReady =
    (legacyXrayNewContainer || isCompletePublicationDestination(destination)) &&
    isGenerationDestinationPublishable(current?.generationMeta);
  const destinationLabel = legacyXrayNewContainer
    ? `${targetLabel} · ${run.xray_output_new_test_set_summary ?? run.xray_output_new_test_plan_summary}`
    : publicationDestinationLabel(destination, targetLabel);

  // Reset draft feedback AND selection (default = all) whenever the proposed version changes (a
  // refine/synthesis completed and the stream refetched). Otherwise index-keyed comments/selection
  // could be misapplied to a different set/order of cases in the new version. useLayoutEffect (not
  // useEffect) so selection is initialized before paint — avoids a flash of "0 of N selected" and a
  // spurious approve warning on the first frame after proposals load.
  useLayoutEffect(() => {
    setConfirmOpen(false);
    setOverall('');
    setComments({});
    setFeedbackOpen(false);
    setFeedbackCaseIndex(null);
    setFocusCaseIndex(null);
    setVariableChoices(null);
    setInitializedVariableKey(null);
    setInitializedSelectionKey(selectionKey);
    setSelected(new Set(available));
    const refsByCase: Record<number, string[]> = {};
    current?.cases.forEach((c, index) => {
      refsByCase[index] = storyLinkRefs(c.storyLinks);
    });
    setRetainedRefsByCase(refsByCase);
    setIncludeSuggestedStoryLinks(true);
    setDestination(defaultPublicationDestination(run, current));
    pendingBatch.current = null;
  }, [current?.id, current?.version, run.id]);

  useLayoutEffect(() => {
    if (
      !v4 ||
      initializedSelectionKey !== selectionKey ||
      (variableChoices !== null &&
        initializedVariableKey === variableNamespaceKey) ||
      !basket.data ||
      basket.isFetching ||
      basket.isError ||
      basket.data.version !== current?.version ||
      JSON.stringify(basket.data.selection) !== JSON.stringify(basketSelection)
    )
      return;
    const choices = basket.data.selection.selectedIndices
      .map((caseIndex) => ({
        caseIndex,
        variables: basket
          .data!.variables.filter(
            (variable) =>
              variable.state !== 'blocked' &&
              variable.caseIndices.includes(caseIndex) &&
              (variableChoices
                ?.find((choice) => choice.caseIndex === caseIndex)
                ?.variables.some((choice) => choice.key === variable.key) ??
                true),
          )
          .map((variable) => ({
            key: variable.key,
            ...(variable.state === 'reuse_candidate'
              ? { reuseExisting: true }
              : {}),
          })),
        ...(variableChoices?.find((choice) => choice.caseIndex === caseIndex)
          ?.disabledExistingKeys?.length
          ? {
              disabledExistingKeys: variableChoices.find(
                (choice) => choice.caseIndex === caseIndex,
              )!.disabledExistingKeys,
            }
          : {}),
      }))
      .filter(
        (choice) =>
          choice.variables.length > 0 ||
          variableChoices?.some(
            (previous) => previous.caseIndex === choice.caseIndex,
          ),
      );
    setVariableChoices(choices);
    setInitializedVariableKey(variableNamespaceKey);
  }, [
    v4,
    initializedSelectionKey,
    selectionKey,
    variableChoices,
    initializedVariableKey,
    variableNamespaceKey,
    basket.data,
    basket.isFetching,
    basket.isError,
    current?.version,
    JSON.stringify(basketSelection),
    availableSignature,
  ]);

  useLayoutEffect(() => {
    if (
      confirmOpen &&
      v4 &&
      confirmedPreviewDigest.current !== basket.data?.digest
    ) {
      setConfirmOpen(false);
    }
  }, [basket.data?.digest, confirmOpen, v4]);

  useLayoutEffect(() => {
    setConfirmOpen(false);
    const stillAvailable = new Set(available);
    setSelected(
      (previous) =>
        new Set([...previous].filter((index) => stillAvailable.has(index))),
    );
  }, [availableSignature]);

  const noneSelected = selected.size === 0;
  const pageSelectedCount = catalog.visibleIndices.filter((index) =>
    selected.has(index),
  ).length;
  const feedbackCount =
    (overall.trim() ? 1 : 0) +
    Object.entries(comments).filter(
      ([index, comment]) => selected.has(Number(index)) && comment.trim(),
    ).length;

  function toggleCase(index: number, on: boolean): void {
    setSelected((prev) => {
      const next = new Set(prev);
      if (on) next.add(index);
      else next.delete(index);
      return next;
    });
  }

  function toggleAll(on: boolean): void {
    setSelected(on ? new Set(available) : new Set());
  }

  function linksForCase(c: ProposalCase, index: number): ProposedStoryLink[] {
    const retainedRefs = retainedRefsByCase[index];
    const storyLinks = c.storyLinks ?? [];
    const refs = retainedRefs ?? storyLinkRefs(storyLinks);
    return storyLinks.filter((link) => refs.includes(link.ref));
  }

  function removeStoryLink(index: number, ref: string): void {
    setRetainedRefsByCase((previous) => {
      const currentCase = current?.cases[index];
      const refs = previous[index] ?? storyLinkRefs(currentCase?.storyLinks);
      return {
        ...previous,
        [index]: refs.filter((candidate) => candidate !== ref),
      };
    });
  }

  function toggleSuggestedStoryLinks(includeAll: boolean): void {
    setIncludeSuggestedStoryLinks(includeAll);
    if (!includeAll) return;
    setRetainedRefsByCase((previous) => {
      const restored = { ...previous };
      current?.cases.forEach((currentCase, index) => {
        restored[index] = storyLinkRefs(currentCase.storyLinks);
      });
      return restored;
    });
  }

  // Step 1: validate the selection, then open the final-confirmation dialog.
  function onApprove(): void {
    if (!canCreateCases) return;
    if (busy) {
      notify.warning('Another action is in progress. Wait for it to finish.');
      return;
    }
    if (noneSelected) {
      notify.warning('Select at least one case to create.');
      return;
    }
    if (!destinationReady) {
      notify.warning('Select an existing destination for these cases.');
      return;
    }
    if (!basketReady) {
      notify.warning(
        basket.isError
          ? `${basket.error.message} Refresh preview and try again.`
          : 'The selected-case preview is updating. Wait for it to finish and try again.',
      );
      return;
    }
    if (
      v4 &&
      run.xray_output_new_test_set_summary &&
      run.xray_output_new_test_plan_summary
    ) {
      notify.warning(
        'Choose one new Xray container: a test set or a test plan.',
      );
      return;
    }
    confirmedPreviewDigest.current = v4 ? (basket.data?.digest ?? null) : null;
    setConfirmOpen(true);
  }

  // Step 2: the reviewer confirmed — create the selected cases (this is irreversible).
  function doApprove(): void {
    if (!current || !canCreateCases) return;
    if (busy) {
      notify.warning('Another action is in progress. Wait for it to finish.');
      return;
    }
    if (v4 && confirmedPreviewDigest.current !== basket.data?.digest) {
      setConfirmOpen(false);
      notify.warning('The preview changed. Review it before creating cases.');
      return;
    }
    const caseIndices =
      v4 && basket.data
        ? basket.data.selection.selectedIndices
        : [...selected].sort((a, b) => a - b);
    const storyLinksByCase = includeSuggestedStoryLinks
      ? caseIndices.map((caseIndex) => ({
          caseIndex,
          refs:
            retainedRefsByCase[caseIndex] ??
            storyLinkRefs(current.cases[caseIndex]?.storyLinks),
        }))
      : undefined;
    if (legacyXrayNewContainer && !v4) {
      approveRun.mutate(
        {
          expectedProposalId: current.id,
          expectedProposalVersion: current.version,
          caseIndices,
          storyLinksByCase:
            storyLinksByCase ??
            caseIndices.map((caseIndex) => ({ caseIndex, refs: [] })),
        },
        {
          onSuccess: () => {
            setConfirmOpen(false);
            notify.success(`Creating selected cases in ${destinationLabel}.`);
          },
          onError: (error) =>
            notify.error(errMsg(error, 'Failed to create cases')),
        },
      );
      return;
    }
    if (!basketReady) {
      notify.warning(
        'Wait for the current variable preview before creating cases.',
      );
      return;
    }
    const payload = {
      ...(v4 && basket.data
        ? {
            generationVersion: basket.data.version,
            previewDigest: basket.data.digest,
            variableSelection: basket.data.selection,
          }
        : {}),
      proposalId: current.id,
      caseIndices,
      destination: legacyXrayNewContainer
        ? {
            provider: 'xray' as const,
            projectKey: run.xray_output_project_key!,
            newContainerKind: run.xray_output_new_test_set_summary
              ? ('testSet' as const)
              : ('testPlan' as const),
            newContainerSummary:
              run.xray_output_new_test_set_summary ??
              run.xray_output_new_test_plan_summary!,
          }
        : destination!,
      includeSuggestedStoryLinks,
      storyLinksByCase,
    };
    const fingerprint = JSON.stringify(payload);
    const request =
      pendingBatch.current?.fingerprint === fingerprint
        ? pendingBatch.current.request
        : { ...payload, idempotencyKey: publicationIdempotencyKey() };
    pendingBatch.current = { fingerprint, request };
    createBatch.mutate(request, {
      onSuccess: (result) => {
        pendingBatch.current = null;
        setConfirmOpen(false);
        notify.success(
          `Creating selected cases in ${targetLabel}. The remaining cases stay available here.`,
        );
        const skipped = result.skipped_existing ?? [];
        if (skipped.length > 0) {
          notify.info(
            `${skipped.length} case${skipped.length === 1 ? '' : 's'} skipped — already ${skipped.length === 1 ? 'exists' : 'exist'} in the target.`,
          );
        }
      },
      onError: (e) => {
        if (
          (e as QualityApiError).details?.code === 'generation_preview_changed'
        ) {
          pendingBatch.current = null;
          setConfirmOpen(false);
          setVariableNamespaceRevision((value) => value + 1);
          notify.warning(
            'The preview changed. Review the updated cases and variables, then confirm creation again.',
          );
          return;
        }
        notify.error(errMsg(e, 'Failed to create cases'));
      },
    });
  }

  async function resumeBatch(batchId: string): Promise<void> {
    if (busy) {
      notify.warning('Another action is in progress. Wait for it to finish.');
      return;
    }
    if (!current || !canResumePublication) return;
    setResumingBatch(batchId);
    try {
      const result = await resumeGenerationPublication(
        runId,
        current.id,
        batchId,
      );
      await refetch?.();
      if (result.batch.status === 'succeeded') {
        notify.success('The saved publication completed.');
      } else {
        notify.warning(
          result.batch.status === 'publishing'
            ? 'Publication is still in progress. Check the saved batch outcomes.'
            : 'Publication remains incomplete. Confirmed cases are kept; inspect the saved batch outcomes.',
        );
      }
    } catch (error) {
      notify.error(errMsg(error, 'Publication could not be resumed'));
    } finally {
      setResumingBatch(null);
    }
  }

  function openFeedback(
    caseIndex: number | null,
    trigger: HTMLButtonElement,
  ): void {
    if (busy) {
      notify.warning('Another action is in progress. Wait for it to finish.');
      return;
    }
    feedbackTriggerRef.current = trigger;
    setFeedbackCaseIndex(caseIndex);
    setFeedbackOpen(true);
    onOpenAssistant?.();
  }

  function onRequestChanges(): void {
    if (busy) {
      notify.warning('Another action is in progress. Wait for it to finish.');
      return;
    }
    if (!current || !canRequestChanges) {
      notify.warning(
        'Wait for the current revision to finish before requesting changes.',
      );
      return;
    }
    const indices =
      feedbackCaseIndex === null
        ? [...selected].sort((left, right) => left - right)
        : available.includes(feedbackCaseIndex)
          ? [feedbackCaseIndex]
          : [];
    if (!indices.length) {
      notify.warning('Select at least one case to revise.');
      return;
    }
    const scope = new Set(indices);
    const perCase = Object.entries(comments)
      .filter(([index, comment]) => scope.has(Number(index)) && comment.trim())
      .map(([index, comment]) => ({
        targetIndex: Number(index),
        comment: comment.trim(),
      }));
    const overallFeedback = feedbackCaseIndex === null ? overall.trim() : '';
    if (!overallFeedback && !perCase.length) {
      notify.warning(
        'Add an overall note or a comment on at least one selected case.',
      );
      return;
    }
    requestChanges.mutate(
      {
        ...(feedbackCaseIndex !== null || onOpenAssistant
          ? {
              expectedProposalId: current.id,
              expectedProposalVersion: current.version,
            }
          : {}),
        ...(overallFeedback ? { overall: overallFeedback } : {}),
        ...(perCase.length ? { perCase } : {}),
        caseIndices: indices,
      },
      {
        onSuccess: () => {
          notify.success('Generating a revised version from your feedback…');
          setFeedbackOpen(false);
          if (feedbackCaseIndex === null) {
            setOverall('');
            setComments({});
          }
        },
        onError: (error) =>
          notify.error(errMsg(error, 'Failed to request changes')),
      },
    );
  }

  useLayoutEffect(() => {
    if (focusCaseIndex === null) return;
    const title = caseTitleRefs.current.get(focusCaseIndex);
    if (!title) return;
    title.focus({ preventScroll: true });
    title.scrollIntoView?.({ block: 'nearest' });
    setFocusCaseIndex(null);
  }, [focusCaseIndex, catalog.page]);

  function inspectCase(index: number): void {
    catalog.reveal(index);
    setFocusCaseIndex(index);
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          Loading proposed cases…
        </CardContent>
      </Card>
    );
  }
  if (isError) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-destructive">
          Couldn&apos;t load the proposed cases. Refresh the page to try again.
        </CardContent>
      </Card>
    );
  }
  if (!current) {
    return (
      <Card>
        <CardContent className="py-6 text-sm text-muted-foreground">
          No proposed cases to review yet.
        </CardContent>
      </Card>
    );
  }

  const unselectedCount = available.length - selected.size;
  const noteSources = (current.generationMeta?.sourceReceipts ?? []).filter(
    (source) =>
      !(source.group === 'workspace_memory' && source.collection === 'empty'),
  );
  const hasSuggestedStoryLinks = available.some(
    (index) => (current.cases[index]?.storyLinks?.length ?? 0) > 0,
  );
  const allSuggestedStoryLinksIncluded =
    includeSuggestedStoryLinks &&
    available.every((index) => {
      const canonicalRefs = storyLinkRefs(current.cases[index]?.storyLinks);
      const retainedRefs = retainedRefsByCase[index] ?? canonicalRefs;
      return canonicalRefs.every((ref) => retainedRefs.includes(ref));
    });

  const previewError = v4 && basket.isError ? basket.error.message : null;
  const reportedCaseNumber = previewError?.match(/^Case (\d+)\b/)?.[1];
  const reportedCaseIndex = reportedCaseNumber
    ? Number(reportedCaseNumber) - 1
    : null;
  const blockingCaseIndex =
    reportedCaseIndex !== null &&
    selected.has(reportedCaseIndex) &&
    available.includes(reportedCaseIndex)
      ? reportedCaseIndex
      : null;
  const saveReasonId = `generation-save-reason-${runId}`;
  const saveReason =
    available.length === 0
      ? ''
      : (previewError ??
        (noneSelected
          ? 'Select at least one case to create.'
          : !basketReady
            ? 'Updating selected cases and variable preview…'
            : !destinationReady
              ? 'Select an active destination before saving.'
              : ''));

  return (
    <>
      {current.generationMeta?.existingCoverage?.length ? (
        <section
          aria-label="Existing coverage"
          className="space-y-2 rounded-lg border p-3 text-sm"
        >
          <h3 className="font-medium">Existing tests cover these behaviors</h3>
          <p>
            No new cases are required for the mapped coverage. Inspect the
            source evidence before relying on it.
          </p>
          {current.generationMeta?.existingCoverage.map((mapping, index) => (
            <details key={index}>
              <summary className="cursor-pointer">
                {mapping.behavior} · {mapping.existing_case_ids.join(', ')}
              </summary>
              <blockquote className="whitespace-pre-wrap border-l-2 pl-3">
                {mapping.quote}
              </blockquote>
              <p className="text-xs">
                Evidence: {mapping.evidence_ids.join(', ')}
              </p>
            </details>
          ))}
        </section>
      ) : null}
      {current.generationMeta?.sizingLimit?.reached && (
        <p role="status">
          The internal safety limit of{' '}
          {current.generationMeta.sizingLimit.hardCap} new cases was reached.
          Review remaining coverage gaps before narrowing or launching another
          run.
        </p>
      )}
      <Card className="gap-3">
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 px-4 pt-4">
          <CardTitle
            ref={catalog.headingRef}
            tabIndex={-1}
            className="flex flex-wrap items-center gap-2"
          >
            <SparklesIcon className="h-4 w-4" aria-hidden />
            Generated case catalog
            <GenerationVersionBadge version={current.version} />
            <Badge variant="secondary">
              {available.length} case{available.length === 1 ? '' : 's'}{' '}
              remaining
            </Badge>
          </CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            {v4 || Boolean(current.generationMeta?.sourceReceipts?.length) ? (
              <GenerationEvidence
                compact
                runId={runId}
                proposalId={current.id}
                version={current.version}
                sources={current.generationMeta?.sourceReceipts}
              />
            ) : null}
            <GenerationDraftWarnings
              cases={current.cases}
              message={generationNotice}
              additionalWarnings={[
                ...(basket.data?.warnings ?? []),
                ...noteSources.flatMap((source) =>
                  source.reason
                    ? [`${source.label}: ${source.reason.replaceAll('_', ' ')}`]
                    : [],
                ),
              ]}
              truncatedCount={
                current.generationMeta?.evidenceReceipt?.truncatedCount
              }
              contextPartial={noteSources.some(
                (source) =>
                  source.collection === 'partial' ||
                  ['partial', 'not_forwarded'].includes(source.consumption),
              )}
            />
            {canRequestChanges && !onOpenAssistant ? (
              <Button
                variant="outline"
                size="sm"
                aria-label="Request changes"
                onClick={(event) => openFeedback(null, event.currentTarget)}
              >
                <MessageSquareIcon aria-hidden /> Request changes
                {feedbackCount > 0 ? (
                  <Badge variant="secondary">
                    {feedbackCount} note{feedbackCount === 1 ? '' : 's'}
                  </Badge>
                ) : null}
              </Button>
            ) : null}
            <Button
              variant="ghost"
              size="icon"
              aria-label="About saving generated cases"
              ref={metaTriggerRef}
              onClick={() => setMetaOpen(true)}
            >
              <InfoIcon className="size-4" aria-hidden />
            </Button>
            <AppDialog
              open={metaOpen}
              onOpenChange={setMetaOpen}
              title="About saving generated cases"
              description="Review generation context and saving details."
              onOpenAutoFocus={() => undefined}
              onCloseAutoFocus={(event) => {
                event.preventDefault();
                metaTriggerRef.current?.focus();
              }}
            >
              {available.length > 0 && canCreateCases ? (
                <p className="text-sm text-muted-foreground">
                  Create any subset in {targetLabel}. Cases you leave unchecked
                  stay available for a later batch, and each batch can use a
                  different existing destination.
                </p>
              ) : null}
              <GenerationMetaReview
                meta={current.generationMeta}
                selectedCoverage={v4 && available.length > 0}
                coverage={basketReady ? basket.data?.coverage : undefined}
              />
            </AppDialog>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col gap-3 px-4 [&:last-child]:pb-4">
          {!isGenerationDestinationPublishable(current.generationMeta) &&
          current.generationMeta?.destination ? (
            <p role="alert" className="text-sm text-destructive">
              This destination is{' '}
              {current.generationMeta.destination.lifecycleState}. Select or
              configure another active destination before the next publication
              attempt.
            </p>
          ) : null}
          {!current.cases.length &&
          current.refinement_state !== 'collecting' ? (
            <p role="status" className="text-[12px] text-muted-foreground">
              This draft has no test cases to revise. Edit the request or
              context and start a new generation.
            </p>
          ) : current.refinement_state === 'unavailable' ? (
            <p role="status" className="text-[12px] text-muted-foreground">
              The original inputs were not retained for this draft. Start a new
              generation to request changes. Saved cases remain available.
            </p>
          ) : current.refinement_state === 'collecting' ? (
            <p role="status" className="text-[12px] text-muted-foreground">
              Original inputs are still being collected. Request changes will
              become available when collection finishes.
            </p>
          ) : null}
          {available.length > 0 && !canCreateCases ? (
            <p role="status" className="text-xs text-muted-foreground">
              This saved draft is available for inspection. This run no longer
              accepts new case publication.
            </p>
          ) : null}
          {v4 ? (
            <>
              <KnowledgeSearchSummary
                context={current.generationMeta?.frozenInput?.knowledgeContext}
              />
            </>
          ) : null}

          {v4 &&
            current.batches
              ?.filter(
                (batch) =>
                  batch.generation_snapshot && batch.status !== 'succeeded',
              )
              .map((batch) => (
                <div
                  key={batch.id}
                  className="rounded-md border border-border p-3 text-xs"
                >
                  <p>
                    Saved publication · {batch.status}. Its cases and variable
                    choices are already fixed.
                  </p>
                  <p className="mt-1 text-muted-foreground">
                    Confirmed creations are kept. An uncertain external creation
                    needs inspection before another attempt.
                  </p>
                  {batch.status === 'partial' || batch.status === 'failed' ? (
                    <p role="alert" className="mt-1 text-destructive">
                      Publication is incomplete. Inspect failed or uncertain
                      case outcomes before retrying.
                    </p>
                  ) : null}
                  {current.publications
                    ?.filter((publication) => publication.batch_id === batch.id)
                    .map((publication) => (
                      <div
                        key={publication.id}
                        className="mt-2 rounded border border-border p-2"
                      >
                        <p>
                          Case {publication.case_index + 1} · create:{' '}
                          {publication.case_state} · placement:{' '}
                          {publication.placement_state}
                        </p>
                        {publication.external_case_key ||
                        publication.external_case_id ? (
                          <p>
                            Confirmed identity:{' '}
                            {publication.external_case_key ??
                              publication.external_case_id}
                          </p>
                        ) : null}
                        {publication.case_error_message ? (
                          <p role="alert">{publication.case_error_message}</p>
                        ) : null}
                        {publication.placement_error_message ? (
                          <p role="alert">
                            {publication.placement_error_message}
                          </p>
                        ) : null}
                        {publication.relation_outcomes.map((outcome) => (
                          <p key={outcome.ref}>
                            Story {outcome.ref} · {outcome.state}
                            {outcome.reason_code
                              ? ` · ${outcome.reason_code.replaceAll('_', ' ')}`
                              : ''}
                          </p>
                        ))}
                      </div>
                    ))}
                  {canResumePublication ? (
                    <Button
                      className="mt-2"
                      variant="outline"
                      size="sm"
                      onClick={() => void resumeBatch(batch.id)}
                    >
                      {resumingBatch === batch.id
                        ? 'Checking publication…'
                        : 'Resume saved publication'}
                    </Button>
                  ) : null}
                </div>
              ))}

          {current.story_linking?.alert?.message &&
          (current.story_linking.alert.code !==
            'story_linking_local_destination' ||
            hasSuggestedStoryLinks) ? (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-info-500/30 bg-info-500/10 px-3 py-2 text-[12px] text-info-600"
            >
              <CircleAlertIcon
                className="mt-0.5 h-4 w-4 shrink-0"
                aria-hidden
              />
              <span>{current.story_linking.alert.message}</span>
            </div>
          ) : null}

          {available.length > 0 ? (
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2 text-xs">
                <label className="flex min-h-8 shrink-0 items-center gap-2 font-medium">
                  <Checkbox
                    aria-label={
                      available.length > 10 ? 'Select this page' : 'Selection'
                    }
                    checked={
                      pageSelectedCount === catalog.visibleIndices.length
                        ? true
                        : pageSelectedCount === 0
                          ? false
                          : 'indeterminate'
                    }
                    onCheckedChange={(checked) =>
                      setSelected((previous) => {
                        const next = new Set(previous);
                        catalog.visibleIndices.forEach((index) => {
                          if (checked === true) next.add(index);
                          else next.delete(index);
                        });
                        return next;
                      })
                    }
                  />
                  {available.length > 10 ? 'This page' : 'Selection'}
                </label>
                <span>
                  {selected.size} selected
                  {available.length > 10 ? ' across all pages' : ''}
                </span>
                {available.length > 10 ? (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 gap-1 px-2"
                      >
                        Selection{' '}
                        <ChevronDownIcon className="size-3" aria-hidden />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onSelect={() => toggleAll(true)}>
                        Select all remaining cases
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => toggleAll(false)}>
                        Clear selection
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : null}
              </div>
              <GenerationCatalogPagination
                page={catalog.page}
                pageCount={catalog.pageCount}
                count={available.length}
                onPage={catalog.navigate}
                label="Draft cases, top"
              />
            </div>
          ) : null}

          <ul className="divide-y divide-border">
            {catalog.visibleIndices.map((i) => {
              const c =
                basket.data?.cases.find((item) => item.caseIndex === i)
                  ?.rendered ?? current.cases[i]!;
              return (
                <ProposedCaseRow
                  key={`${current.id}-${i}`}
                  index={i}
                  open={catalog.isExpanded(i)}
                  onOpenChange={(open) => catalog.setExpanded(i, open)}
                  titleRef={(element) => {
                    if (element) caseTitleRefs.current.set(i, element);
                    else caseTitleRefs.current.delete(i);
                  }}
                  needsAttention={blockingCaseIndex === i}
                  onRequestChanges={
                    onOpenAssistant
                      ? undefined
                      : (trigger) => openFeedback(i, trigger)
                  }
                  c={c}
                  executionContext={
                    current.generationMeta?.frozenInput?.executionContext
                  }
                  evidenceScope={
                    v4
                      ? {
                          runId,
                          proposalId: current.id,
                          version: current.version,
                          sources: current.generationMeta?.sourceReceipts,
                        }
                      : undefined
                  }
                  storyLinks={
                    includeSuggestedStoryLinks ? linksForCase(c, i) : []
                  }
                  selected={selected.has(i)}
                  onSelect={(on) => toggleCase(i, on)}
                  onRemoveStoryLink={(ref) => removeStoryLink(i, ref)}
                  comment={comments[i] ?? ''}
                  onComment={(v) => setComments((m) => ({ ...m, [i]: v }))}
                  canComment={canRequestChanges}
                />
              );
            })}
          </ul>
          <GenerationCatalogPagination
            page={catalog.page}
            pageCount={catalog.pageCount}
            count={available.length}
            onPage={catalog.navigate}
            label="Draft cases, bottom"
          />

          {v4 && available.length > 0 && !basket.isError ? (
            <GenerationBasket
              showWarnings={false}
              preview={variableChoices === null ? undefined : basket.data}
              loading={basket.isFetching || variableChoices === null}
              onToggle={toggleVariable}
              onToggleExisting={toggleExistingVariable}
            />
          ) : null}

          <div className="space-y-3 rounded-lg border border-border bg-card p-3 lg:sticky lg:bottom-14 lg:z-10 lg:shadow-elev-1">
            {saveReason ? (
              <div
                id={saveReasonId}
                role={previewError ? 'alert' : 'status'}
                aria-label={
                  previewError ? 'Cannot prepare selected cases' : undefined
                }
                className="text-xs"
              >
                {previewError ? (
                  <p className="font-medium text-destructive">
                    Cannot prepare selected cases
                  </p>
                ) : null}
                <p
                  className={
                    previewError
                      ? 'mt-1 break-words text-destructive'
                      : 'text-muted-foreground'
                  }
                >
                  {saveReason}
                </p>
                {previewError ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {blockingCaseIndex !== null ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => inspectCase(blockingCaseIndex)}
                      >
                        Inspect case #{blockingCaseIndex + 1}
                      </Button>
                    ) : null}
                    {canRequestChanges && !onOpenAssistant ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={(event) =>
                          openFeedback(blockingCaseIndex, event.currentTarget)
                        }
                      >
                        Request changes
                        {blockingCaseIndex !== null
                          ? ` to case #${blockingCaseIndex + 1}`
                          : ' to selected cases'}
                      </Button>
                    ) : null}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() =>
                        setVariableNamespaceRevision((value) => value + 1)
                      }
                    >
                      Refresh preview
                    </Button>
                  </div>
                ) : null}
              </div>
            ) : null}
            {available.length > 0 && canCreateCases ? (
              <p className="text-xs text-muted-foreground">
                {selected.size} selected for creation
              </p>
            ) : null}

            {available.length > 0 &&
            canCreateCases &&
            legacyXrayNewContainer ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-[11px] text-muted-foreground">
                  This run creates its Xray container as one operation, so it
                  uses the destination selected when generation started.
                </p>
                <Button
                  variant="brand"
                  size="sm"
                  className="ml-auto shrink-0"
                  aria-describedby={saveReason ? saveReasonId : undefined}
                  onClick={onApprove}
                >
                  <CheckCircle2Icon aria-hidden />
                  {createBatch.isPending || approveRun.isPending
                    ? 'Creating…'
                    : current.status === 'proposed'
                      ? 'Approve & create'
                      : 'Create selected'}{' '}
                  ({selected.size})
                </Button>
              </div>
            ) : available.length > 0 && canCreateCases ? (
              <div className="flex flex-wrap items-end justify-between gap-3">
                <div className="flex w-full min-w-0 basis-full flex-wrap items-end gap-3 lg:w-auto lg:basis-[32rem] lg:flex-1">
                  <PublicationDestinationPicker
                    run={run}
                    value={destination}
                    onChange={setDestination}
                  />
                  {hasSuggestedStoryLinks ? (
                    <label className="flex min-h-8 items-center gap-2 text-[11px] font-medium">
                      <Checkbox
                        checked={allSuggestedStoryLinksIncluded}
                        onCheckedChange={(value) =>
                          toggleSuggestedStoryLinks(value === true)
                        }
                        aria-label="Include suggested story links"
                      />
                      Include all suggested story links
                    </label>
                  ) : null}
                </div>
                <Button
                  variant="brand"
                  size="sm"
                  className="ml-auto shrink-0"
                  aria-describedby={saveReason ? saveReasonId : undefined}
                  onClick={onApprove}
                >
                  <CheckCircle2Icon aria-hidden />
                  {createBatch.isPending || approveRun.isPending
                    ? 'Creating…'
                    : current.status === 'proposed'
                      ? 'Approve & create'
                      : 'Create selected'}{' '}
                  ({selected.size})
                </Button>
              </div>
            ) : available.length === 0 ? (
              <p
                className={
                  current.cases.length === 0
                    ? 'rounded-md bg-muted/50 px-3 py-2 text-[12px] text-muted-foreground'
                    : 'rounded-md border border-success-500/30 bg-success-500/10 px-3 py-2 text-[12px] text-success-600'
                }
              >
                {v4 &&
                current.batches?.some((batch) => batch.status !== 'succeeded')
                  ? 'All selected cases are in saved publication batches. Check their outcomes below.'
                  : current.cases.length === 0
                    ? current.generationMeta?.existingCoverage?.length
                      ? 'No new cases were needed for the mapped existing coverage.'
                      : 'No test cases were produced. Check the received data and generation details.'
                    : 'All generated cases have been created.'}
              </p>
            ) : null}
          </div>
        </CardContent>
      </Card>

      <FeedbackSurface
        container={feedbackContainer}
        inline={Boolean(onOpenAssistant)}
        title={
          feedbackCaseIndex === null
            ? 'Request changes'
            : `Request changes to case #${feedbackCaseIndex + 1}`
        }
        onFocusInput={() => feedbackRef.current?.focus()}
        onFocusTrigger={(fallback) => {
          const trigger = feedbackTriggerRef.current;
          (trigger?.isConnected ? trigger : fallback)?.focus();
        }}
        onOpen={(trigger) => {
          if (busy) {
            notify.warning(
              'Wait for the current action to finish, then try again.',
            );
            return;
          }
          openFeedback(null, trigger);
        }}
        open={feedbackOpen && canRequestChanges}
        available={canRequestChanges}
        onOpenChange={setFeedbackOpen}
      >
        <div
          className={
            onOpenAssistant ? 'flex flex-col gap-2' : 'flex flex-col gap-3'
          }
        >
          {onOpenAssistant && (
            <>
              <Select
                value={
                  feedbackCaseIndex === null
                    ? 'selected'
                    : String(feedbackCaseIndex)
                }
                onValueChange={(value) =>
                  setFeedbackCaseIndex(
                    value === 'selected' ? null : Number(value),
                  )
                }
              >
                <SelectTrigger aria-label="Cases to revise">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="selected">
                    Selected cases ({selected.size})
                  </SelectItem>
                  {available.map((index) => (
                    <SelectItem key={index} value={String(index)}>
                      Case #{index + 1}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          )}
          <label htmlFor="review-overall" className="text-sm font-medium">
            {feedbackCaseIndex === null
              ? 'Feedback for selected cases'
              : `Feedback for case #${feedbackCaseIndex + 1}`}
          </label>
          <Textarea
            ref={feedbackRef}
            id="review-overall"
            rows={onOpenAssistant ? 2 : 4}
            value={
              feedbackCaseIndex === null
                ? overall
                : (comments[feedbackCaseIndex] ?? '')
            }
            onChange={(event) =>
              feedbackCaseIndex === null
                ? setOverall(event.target.value)
                : setComments((previous) => ({
                    ...previous,
                    [feedbackCaseIndex]: event.target.value,
                  }))
            }
            aria-describedby={
              onOpenAssistant ? undefined : 'review-overall-hint'
            }
            placeholder={
              onOpenAssistant
                ? 'e.g. Add a negative case'
                : 'e.g. add more negative-path cases; merge duplicates; remove a case; fix missing source evidence'
            }
          />
          {!onOpenAssistant && (
            <p
              id="review-overall-hint"
              className="text-xs text-muted-foreground"
            >
              {feedbackCaseIndex === null
                ? `${selected.size} selected across all pages. Other cases stay unchanged.`
                : `Only case #${feedbackCaseIndex + 1} will be revised. Other cases stay unchanged.`}
            </p>
          )}
          <div className="flex flex-nowrap justify-end gap-2">
            <Button variant="outline" onClick={() => setFeedbackOpen(false)}>
              Cancel
            </Button>
            <Button variant="brand" onClick={onRequestChanges}>
              Request changes ({feedbackCaseIndex === null ? selected.size : 1})
            </Button>
          </div>
        </div>
      </FeedbackSurface>

      {/* Final confirmation keeps the external write explicit while unselected cases remain reusable. */}
      <AppDialog
        open={confirmOpen}
        onOpenChange={(o) => (o ? setConfirmOpen(true) : setConfirmOpen(false))}
        title="Create test cases?"
        description={`Final review before cases are created in ${destinationLabel}.`}
        style={{ maxWidth: 460 }}
      >
        <div className="flex flex-col gap-3">
          <p className="text-[13px] font-medium">
            This is the final review screen.
          </p>
          <p className="text-[12px] text-muted-foreground">
            You&apos;re about to create{' '}
            <span className="font-medium text-foreground">{selected.size}</span>{' '}
            of {available.length} available test case
            {available.length === 1 ? '' : 's'} in {destinationLabel}.
            {unselectedCount > 0
              ? ` The remaining ${unselectedCount} unselected case${unselectedCount === 1 ? '' : 's'} will stay available.`
              : ''}
          </p>
          {v4 && basket.data ? (
            <div className="rounded border border-border p-2 text-xs">
              <p>Variables saved in Quality with these cases:</p>
              {basket.data.variables
                .filter((variable) => variable.enabledCaseIndices.length > 0)
                .map((variable) => (
                  <p key={variable.key}>
                    <strong>{variable.effectiveKey}</strong>:{' '}
                    {variable.defaultValue} ·{' '}
                    {variable.state === 'reuse_candidate'
                      ? 'reuse existing'
                      : 'create'}{' '}
                    · {variable.enabledCaseIndices.length} case(s)
                  </p>
                ))}
              {basket.data.existingVariables
                ?.filter((variable) => variable.enabledCaseIndices.length > 0)
                .map((variable) => (
                  <p key={`existing-${variable.key}`}>
                    <strong>{variable.key}</strong>: {variable.value} · captured
                    existing value · {variable.enabledCaseIndices.length}{' '}
                    case(s)
                  </p>
                ))}
              {!basket.data.variables.some(
                (variable) => variable.enabledCaseIndices.length > 0,
              ) &&
              !basket.data.existingVariables?.some(
                (variable) => variable.enabledCaseIndices.length > 0,
              ) ? (
                <p>None. All selected cases keep literal text.</p>
              ) : null}
            </div>
          ) : null}
          <p className="text-[12px] text-muted-foreground">
            Created cases cannot be edited here. Any unselected cases remain in
            this catalog and can be created later.
          </p>
          <div className="flex justify-end gap-2 border-t border-border pt-3">
            <Button
              variant="outline"
              onClick={() => {
                if (busy) {
                  notify.warning(
                    'Another action is in progress. Wait for it to finish.',
                  );
                  return;
                }
                setConfirmOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="brand" onClick={doApprove}>
              <CheckCircle2Icon aria-hidden />
              {busy ? 'Creating…' : 'Create cases'}
            </Button>
          </div>
        </div>
      </AppDialog>
    </>
  );
}

function ProposedCaseRow({
  index,
  c,
  evidenceScope,
  executionContext,
  storyLinks,
  selected,
  onSelect,
  onRemoveStoryLink,
  comment,
  onComment,
  canComment,
  open,
  onOpenChange,
  needsAttention,
  onRequestChanges,
  titleRef,
}: {
  index: number;
  c: ProposalCase;
  executionContext?: GenerationCredentialContext;
  evidenceScope?: {
    runId: string;
    proposalId: string;
    version: number;
    sources?: GenerationSourceReceipt[];
  };
  storyLinks: ProposedStoryLink[];
  selected: boolean;
  onSelect: (on: boolean) => void;
  onRemoveStoryLink: (ref: string) => void;
  comment: string;
  onComment: (v: string) => void;
  canComment: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  needsAttention: boolean;
  onRequestChanges?: (trigger: HTMLButtonElement) => void;
  titleRef: Ref<HTMLButtonElement>;
}): JSX.Element {
  const detailsId = `proposed-case-${index}-details`;
  return (
    <li className="py-2">
      <div className="flex min-h-8 items-center gap-2">
        <Checkbox
          checked={selected}
          className="relative after:absolute after:-inset-2 after:content-['']"
          onCheckedChange={(v) => onSelect(v === true)}
          aria-label={`Select case #${index + 1}: ${c.title}`}
        />
        <button
          ref={titleRef}
          type="button"
          onClick={() => onOpenChange(!open)}
          aria-expanded={open}
          aria-controls={open ? detailsId : undefined}
          className="flex min-h-8 min-w-0 flex-1 items-center gap-2 text-left"
        >
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${open ? '' : '-rotate-90'}`}
            aria-hidden
          />
          <span className="text-[10px] text-muted-foreground">
            #{index + 1}
          </span>
          <span className="min-w-0 flex-1 line-clamp-2 text-sm font-medium">
            {c.title}
          </span>
          {needsAttention ? (
            <Badge variant="warning" className="shrink-0">
              Needs attention
            </Badge>
          ) : null}
          {/* Only when selected — request-changes ignores comments on unselected cases, so showing
              the badge there would imply the comment counts when it doesn't. */}
          {selected && comment.trim() && (
            <Badge variant="warning" className="shrink-0">
              commented
            </Badge>
          )}
        </button>
      </div>
      {open && (
        <div
          id={detailsId}
          className="flex max-w-4xl flex-col gap-4 pb-3 pt-3 text-[13px] leading-relaxed sm:pl-9"
        >
          {needsAttention ? (
            <p className="text-xs text-warning-700">
              This case needs attention before saving. See the reported issue by
              the save action.
            </p>
          ) : null}
          {storyLinks.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5 px-3 py-2">
              <span className="mr-1 inline-flex items-center gap-1 text-[10px] font-medium text-muted-foreground">
                <Link2Icon className="h-3 w-3" aria-hidden />
                Suggested story links
              </span>
              {storyLinks.map((link) => {
                const safeUrl = safeHttpUrl(link.url);
                return (
                  <Badge
                    key={link.ref}
                    variant="info"
                    className="max-w-full gap-1 pl-2 pr-1 text-[10px]"
                  >
                    {safeUrl ? (
                      <a
                        href={safeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate underline underline-offset-2"
                        title={link.title}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {link.key}
                      </a>
                    ) : (
                      <span className="truncate" title={link.title}>
                        {link.key}
                      </span>
                    )}
                    <span className="max-w-[220px] truncate" title={link.title}>
                      {link.title}
                    </span>
                    <button
                      type="button"
                      aria-label={`Remove suggested story link ${link.key}`}
                      title={`Remove ${link.key}`}
                      className="ml-0.5 inline-flex size-4 shrink-0 items-center justify-center rounded-full hover:bg-info-500/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemoveStoryLink(link.ref);
                      }}
                    >
                      <XIcon className="size-3" aria-hidden />
                    </button>
                  </Badge>
                );
              })}
            </div>
          ) : null}
          {c.precondition ? (
            <GenerationPreconditions
              text={c.precondition}
              context={executionContext}
            />
          ) : null}
          <GenerationCaseSteps testCase={c} context={executionContext} />

          <CaseCoverage c={c} />

          {evidenceScope && c.basis?.length ? (
            <GenerationEvidence {...evidenceScope} basis={c.basis} />
          ) : null}

          {/* Comment for this case — shown alongside the steps only when the row is expanded. */}
          {canComment ? (
            <div className="flex flex-col gap-1 pt-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="font-medium">Comment</span>
                {onRequestChanges && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(event) => onRequestChanges(event.currentTarget)}
                  >
                    Request changes to case #{index + 1}
                  </Button>
                )}
              </div>
              <Textarea
                rows={1}
                disabled={!selected}
                aria-label={`Comment on case #${index + 1}: ${c.title}`}
                placeholder={
                  selected
                    ? 'Comment on this case (optional)…'
                    : 'Select this case to comment / revise it'
                }
                value={comment}
                onChange={(e) => onComment(e.target.value)}
                className="field-sizing-fixed h-9 resize-none overflow-y-auto text-xs"
              />
            </div>
          ) : null}
        </div>
      )}
    </li>
  );
}

function errMsg(e: unknown, fallback: string): string {
  if (e instanceof QualityApiError) return e.message;
  return e instanceof Error ? e.message : fallback;
}

/** Keeps selection, comments and version-bound mutations in the original controller. */
function FeedbackSurface({
  container,
  inline,
  open,
  available,
  onOpenChange,
  children,
  title,
  onFocusInput,
  onFocusTrigger,
  onOpen,
}: {
  title: string;
  onFocusInput: () => void;
  onFocusTrigger: (fallback?: HTMLButtonElement | null) => void;
  onOpen: (trigger: HTMLButtonElement) => void;
  container?: HTMLElement | null;
  inline: boolean;
  open: boolean;
  available: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}): JSX.Element | null {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const wasOpen = useRef(false);
  const focusInput = useRef(onFocusInput);
  const focusTrigger = useRef(onFocusTrigger);
  focusInput.current = onFocusInput;
  focusTrigger.current = onFocusTrigger;
  useLayoutEffect(() => {
    if (!inline || !available) {
      wasOpen.current = false;
      return;
    }
    if (open) focusInput.current();
    else if (wasOpen.current) focusTrigger.current(triggerRef.current);
    wasOpen.current = open;
  }, [inline, available, open]);
  if (!available) return null;
  if (inline)
    return container
      ? createPortal(
          open ? (
            <section aria-label={title} className="basis-full min-w-0 pt-2">
              {children}
            </section>
          ) : (
            <Button
              ref={triggerRef}
              variant="outline"
              size="sm"
              onClick={(event) => onOpen(event.currentTarget)}
            >
              <MessageSquareIcon aria-hidden /> Request changes
            </Button>
          ),
          container,
        )
      : null;
  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      onOpenAutoFocus={(event) => {
        event.preventDefault();
        onFocusInput();
      }}
      onCloseAutoFocus={(event) => {
        event.preventDefault();
        onFocusTrigger();
      }}
      description="Refine the selected cases. Other cases stay unchanged."
    >
      {children}
    </AppDialog>
  );
}
